import { apiClient } from "@/lib/apiClient";

// Collection types and interfaces
export interface Metafield {
  key: string;
  value: string;
  type?: string;
}

export interface Collection {
  id?: string;
  slug?: string;
  title?: string;
  description?: string;
  image?: string;
  status?: "ACTIVE" | "DRAFT" | "ARCHIVED";
  metafields?: Metafield[];
  products?: unknown[];
  total_products?: number;
  products_count?: number;
}

export interface CollectionFilters {
  title?: string;
  description?: string;
  created_after?: string;
  created_before?: string;
}

export interface FetchCollectionsOptions {
  page?: number;
  pageSize?: number;
  filters?: CollectionFilters;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface SearchCollectionsOptions {
  query: string;
  page?: number;
  pageSize?: number;
}

export type CollectionStatus = "ACTIVE" | "DRAFT" | "ARCHIVED";

export interface CreateCollectionRequest {
  status: CollectionStatus;
  title?: string;
  description?: string;
  image?: string;
  product_ids?: string[];
  metafields?: Metafield[];
}

export interface UpdateCollectionRequest
  extends Partial<CreateCollectionRequest> {}

export interface AddProductsToCollectionRequest {
  product_ids: string[];
}

export interface RemoveProductsFromCollectionRequest
  extends Record<string, unknown> {
  product_ids: string[];
}

export interface UploadUrlResponse {
  data: {
    upload_url: string;
    uploaded_file_name: string;
  };
  message: string;
  valid: boolean;
}

// Collection CRUD Operations
export const getCollections = async (options: FetchCollectionsOptions = {}) => {
  const params: Record<string, string> = {};

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.page_size = options.pageSize.toString();
  if (options.sortBy) params.sort_by = options.sortBy;
  if (options.sortOrder) params.sort_order = options.sortOrder;

  // Add filters
  if (options.filters) {
    if (options.filters.title) params.title = options.filters.title;
    if (options.filters.description)
      params.description = options.filters.description;
    if (options.filters.created_after)
      params.created_after = options.filters.created_after;
    if (options.filters.created_before)
      params.created_before = options.filters.created_before;
  }

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/collections",
    params,
  );
};

export const getCollectionById = async (collectionId: string) => {
  return await apiClient.get(`/api/v1/admin/:storeId/collections/${collectionId}`);
};

export const searchCollections = async (options: SearchCollectionsOptions) => {
  const params: Record<string, string> = {
    q: options.query,
  };

  if (options.page) params.page = options.page.toString();
  if (options.pageSize) params.page_size = options.pageSize.toString();

  return await apiClient.getPaginated(
    "/api/v1/admin/:storeId/collections/search",
    params,
  );
};

export const createCollection = async (collection: CreateCollectionRequest) => {
  return await apiClient.post("/api/v1/admin/:storeId/collections", collection);
};

export const updateCollection = async (
  collectionId: string,
  collection: UpdateCollectionRequest,
) => {
  return await apiClient.put(
    `/api/v1/admin/:storeId/collections/${collectionId}`,
    collection,
  );
};

export const deleteCollection = async (collectionId: string) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/collections/${collectionId}`,
  );
};

// Collection-Product Management
export const addProductsToCollection = async (
  collectionId: string,
  request: AddProductsToCollectionRequest,
) => {
  return await apiClient.post(
    `/api/v1/admin/:storeId/collections/${collectionId}/products`,
    request,
  );
};

export const removeProductsFromCollection = async (
  collectionId: string,
  request: RemoveProductsFromCollectionRequest,
) => {
  return await apiClient.delete(
    `/api/v1/admin/:storeId/collections/${collectionId}/products`,
    request,
  );
};

// Asset Management
export const getCollectionAssetUploadUrl = async (
  filename: string,
): Promise<UploadUrlResponse> => {
  return await apiClient.get(
    `/api/v1/admin/:storeId/collections/assets/${filename}`,
  );
};

// Export/Import
export const exportCollections = async () => {
  return await apiClient.get("/api/v1/admin/:storeId/collections/export");
};

export const importCollections = async (file: File) => {
  return await apiClient.uploadFile(
    "/api/v1/admin/:storeId/collections/import",
    file,
  );
};

// Public API helpers (for reference, might be used in frontend)
export const getPublicCollections = async (page = 1, limit = 10) => {
  const params = {
    page: page.toString(),
    limit: limit.toString(),
  };

  return await apiClient.getPaginated(
    "/api/v1/public/:storeId/collections",
    params,
  );
};

export const getPublicCollectionByIdentifier = async (identifier: string) => {
  return await apiClient.get(
    `/api/v1/public/:storeId/collections/${identifier}`,
  );
};
